import { CARD_COLUMN_GAP_PX, cardHeightPx } from "./cards";

/**
 * Окно карточек (DEN-104, design stage9-cards-virtual): в DOM только карточки,
 * пересекающие видимую полосу с запасом, плюс закреплённые — карточки строки
 * с фокусом и её соседей по навигации. Раскладка «masonry» уже знает `top` и
 * высоту каждой карточки (`layoutCards`), так что отбор — арифметика, без
 * чтения DOM.
 */

/** Запас над и под видимой полосой, px: прокрутка колесом не успевает показать пустоту. */
export const CARD_WINDOW_OVERSCAN_PX = 800;
/**
 * Шаг, которым двигаются границы окна, px. Окно пересчитывается не на каждое
 * событие прокрутки, а когда граница переходит через шаг: вид перерисовывается
 * раз в несколько сотен пикселей. Шаг не больше запаса — иначе край окна
 * отставал бы от видимой полосы.
 */
export const CARD_WINDOW_STEP_PX = 400;

export interface CardWindowBounds {
  top: number;
  bottom: number;
}

/**
 * Границы окна в координатах контейнера карточек по видимой полосе
 * `[viewTop, viewBottom]`: полоса, расширенная на запас и округлённая наружу
 * до шага. Пока полоса движется внутри шага, границы прежние.
 */
export function cardWindowBounds(viewTop: number, viewBottom: number): CardWindowBounds {
  return {
    top:
      Math.floor((viewTop - CARD_WINDOW_OVERSCAN_PX) / CARD_WINDOW_STEP_PX) * CARD_WINDOW_STEP_PX,
    bottom:
      Math.ceil((viewBottom + CARD_WINDOW_OVERSCAN_PX) / CARD_WINDOW_STEP_PX) * CARD_WINDOW_STEP_PX,
  };
}

/**
 * Индексы карточек окна по возрастанию: пересекающие `[bounds.top,
 * bounds.bottom]` и закреплённые `pinned` (вне диапазона `0..count-1`
 * отбрасываются).
 *
 * `tops` не убывают по порядку карточек — свойство `layoutCards`: очередная
 * карточка ложится в колонку с наименьшим низом. Поэтому конец окна — бинарный
 * поиск по `top`, а начало — бинарный поиск по `top ≥ bounds.top − самая высокая
 * карточка`: выше неё ни одна карточка до окна не достаёт.
 */
export function cardWindowIndexes(
  tops: readonly number[],
  heights: readonly number[],
  bounds: CardWindowBounds,
  pinned: readonly number[],
): number[] {
  const count = tops.length;
  let tallest = 0;
  for (const height of heights) {
    tallest = Math.max(tallest, height);
  }
  const start = firstIndexWhere(tops, (top) => top >= bounds.top - tallest);
  const end = firstIndexWhere(tops, (top) => top > bounds.bottom);
  const picked = new Set<number>();
  for (let index = start; index < end; index += 1) {
    if ((tops[index] ?? 0) + (heights[index] ?? 0) >= bounds.top) {
      picked.add(index);
    }
  }
  for (const index of pinned) {
    if (Number.isInteger(index) && index >= 0 && index < count) {
      picked.add(index);
    }
  }
  return [...picked].sort((left, right) => left - right);
}

/** Первый индекс, где `reached` истинно; `reached` монотонно по неубывающим `tops`. */
function firstIndexWhere(tops: readonly number[], reached: (top: number) => boolean): number {
  let low = 0;
  let high = tops.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (!reached(tops[middle] ?? 0)) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  return low;
}

/**
 * Сколько первых карточек рисовать, пока раскладка не посчитана (первый кадр
 * в браузере): столько, сколько их помещается одной колонкой в `limitPx`, но
 * не меньше одной. Позиций ещё нет, и этот кадр не доходит до экрана —
 * раскладка считается в layout-эффекте, — но и монтировать весь вид ради
 * него незачем (design stage9-cards-virtual Д5).
 */
export function cardPrefixCount(
  rowCounts: readonly number[],
  remPx: number,
  limitPx: number,
): number {
  let used = 0;
  let count = 0;
  for (const rows of rowCounts) {
    if (count > 0 && used >= limitPx) {
      break;
    }
    used += cardHeightPx(rows, remPx) + CARD_COLUMN_GAP_PX;
    count += 1;
  }
  return count;
}

/**
 * Индекс карточки по индексу строки в плоском списке ключей навигации
 * («карточка за карточкой, строка за строкой»). `-1` — индекс вне списка.
 * `rowCounts` — число строк каждой карточки; карточки без строк ключей не дают.
 */
export function cardIndexOfFlatRow(rowCounts: readonly number[], flatIndex: number): number {
  if (flatIndex < 0) {
    return -1;
  }
  let passed = 0;
  for (let index = 0; index < rowCounts.length; index += 1) {
    passed += rowCounts[index] ?? 0;
    if (flatIndex < passed) {
      return index;
    }
  }
  return -1;
}
